import { useEffect, useLayoutEffect, useState } from 'react';
import type { OrderDetail } from '@/api/types';
import { api, ApiError } from '@/api/client';
import { useFormFactor } from '@/lib/formFactor';
import { Icon } from '@/components/Icon';
import { Dot } from '@/components/Tag';
import { dateTime, money, qty } from '@/lib/format';
import { stockStatus } from '@/lib/status';
import { approveOrderWithFollower, rejectOrder } from './api';
import { summarizeLines } from './OrderCards';
import s from './OrderApproveDialog.module.css';

type Follower = { id: string; name: string; username?: string; activeOrderCount: number; dueThisWeekCount: number };
const READONLY = import.meta.env.VITE_READONLY !== '0';

export function OrderApproveDialog({ order, canApprove, canReject = canApprove, initialRejectMode = false, onClose, onSuccess }: { order: OrderDetail; canApprove: boolean; canReject?: boolean; initialRejectMode?: boolean; onClose: () => void; onSuccess: () => void }) {
  const ff = useFormFactor();
  const tabletPopover = ff === 'tablet' && !initialRejectMode;
  const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number }>();
  const [followers, setFollowers] = useState<Follower[]>([]);
  const [followerId, setFollowerId] = useState(order.followerUser?.id || '');
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [rejected, setRejected] = useState(false);
  const [rejectMode, setRejectMode] = useState(initialRejectMode);
  const [rejectReason, setRejectReason] = useState('');
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState('');
  const close = () => { if (done) onSuccess(); onClose(); };

  useLayoutEffect(() => {
    if (!tabletPopover) return;
    const place = () => {
      const anchor = document.querySelector<HTMLButtonElement>('button[class*="headerPrimary"]');
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const width = 340;
      setPopoverPosition({ top: Math.round(rect.bottom + 9), left: Math.round(Math.min(window.innerWidth - width - 12, Math.max(12, rect.right - width))) });
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [tabletPopover]);

  useEffect(() => {
    const controller = new AbortController();
    void api.get<{ followers: Follower[] }>('/api/v2/orders/assignable-followers', undefined, controller.signal)
      .then((result) => { setFollowers(result.followers || []); setLoading(false); })
      .catch((cause: Error) => { if (cause.name !== 'AbortError') { setListError(cause.message); setLoading(false); } });
    return () => controller.abort();
  }, []);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape' && !saving) close(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, onSuccess, done, saving]);

  const submit = async () => {
    if (READONLY || !canApprove || order.status !== 'SUBMITTED' || !followers.some((user) => user.id === followerId) || saving || uncertain) return;
    setSaving(true);
    setError('');
    try {
      await approveOrderWithFollower(order.id, followerId);
      setDone(true);
    } catch (cause) {
      if (!(cause instanceof ApiError) || cause.status === 0 || cause.status >= 500) setUncertain(true);
      setError(cause instanceof Error ? cause.message : '审核失败');
    } finally { setSaving(false); }
  };
  const reject = async () => {
    if (READONLY || !canReject || order.status !== 'SUBMITTED' || !rejectReason.trim() || saving || uncertain) return;
    setSaving(true); setError('');
    try { await rejectOrder(order.id, rejectReason.trim()); setRejected(true); setDone(true); }
    catch (cause) {
      if (!(cause instanceof ApiError) || cause.status === 0 || cause.status >= 500) setUncertain(true);
      setError(cause instanceof Error ? cause.message : '驳回失败');
    } finally { setSaving(false); }
  };
  const summary = summarizeLines(order.lines);
  const stock = order.stockStatus ? stockStatus[order.stockStatus.status] : null;

  return <div className={`${s.backdrop} ${tabletPopover ? s.tabletBackdrop : ''}`} onMouseDown={(event) => { if (event.target === event.currentTarget && !saving) close(); }}>
    <section className={`${s.dialog} ${tabletPopover ? s.tabletPopover : ''}`} style={tabletPopover && popoverPosition ? { top: popoverPosition.top, left: popoverPosition.left } : undefined} role="dialog" aria-modal={!tabletPopover} aria-label={rejectMode ? '驳回订单' : '审核订单'}>
      <div className={s.grab} aria-hidden="true" />
      <header className={s.head}><button type="button" aria-label="关闭" onClick={close} disabled={saving}>×</button><div><b>{rejectMode ? '驳回订单' : '审核订单'}</b><small>{order.orderNo}</small></div><span /></header>
      {done ? <div className={s.done}><Icon name="check" size={24} /><b>{rejected ? '订单已驳回' : '订单已通过并分配'}</b><p>{rejected ? '库存预占已解除，订单已退回 V1 可编辑草稿。' : 'V1 共用订单已更新，跟单员会收到通知。'}</p><button type="button" onClick={close}>完成</button></div> : <>
        <div className={s.body}>
          <div className={s.summary}>
            <div><span>客户</span><b>{order.customer.name}</b></div>
            {order.salesUser && <div className={s.salesRow}><span>业务员</span><b>{order.salesUser.name}{order.submittedAt ? ` · ${dateTime(order.submittedAt)} 提交` : ''}</b></div>}
            <div><span>订单内容</span><b>{summary.map((line) => `${line.name} × ${line.qty}`).join('；') || '—'}</b></div>
            {order.totalAmount !== undefined && <div><span>订单金额</span><b className={s.amount}>{money(order.totalAmount)}</b></div>}
            <div><span>库存状态</span><b>{stock ? <Dot tone={stock.dot}>{stock.label}{order.stockStatus?.shortageQuantity ? ` · 缺 ${qty(order.stockStatus.shortageQuantity)} 个` : ''}</Dot> : '待核对'}</b></div>
          </div>
          {!rejectMode && <><div className={s.groupTitle}><span className={s.desktopGroupTitle}>* 分配跟单人员</span><span className={s.tabletGroupTitle}>通过并分配跟单</span><small>有跟单权限且账号启用</small></div>
          <select className={s.desktopFollowerSelect} aria-label="分配跟单人员" value={followerId} onChange={(event) => setFollowerId(event.target.value)} disabled={loading || Boolean(listError)}>
            <option value="">选择跟单人员</option>
            {followers.map((user) => <option key={user.id} value={user.id}>{user.name}</option>)}
          </select>
          {loading ? <p className={s.hint}>正在读取可分配人员…</p> : listError ? <p className={s.error} role="alert">人员读取失败：{listError}</p> : followers.length ? <div className={s.followers} role="radiogroup" aria-label="分配跟单人员">{followers.map((user) => <button type="button" role="radio" aria-checked={followerId === user.id} className={followerId === user.id ? s.selected : ''} key={user.id} onClick={() => setFollowerId(user.id)}><span className={s.avatar}>{user.name.slice(0, 1)}</span><span><b>{user.name}</b><small>在跟 {qty(user.activeOrderCount)} 单{user.dueThisWeekCount > 0 ? ` · ${qty(user.dueThisWeekCount)} 单本周交货` : ''}</small></span><i>{followerId === user.id ? '✓' : ''}</i></button>)}</div> : <p className={s.error}>没有可分配的跟单人员。</p>}
          {ff !== 'tablet' && <p className={s.hint}>通过后订单进入备货；库存已齐备的直接标记“配货齐全”。</p>}</>}
          {rejectMode && <label className={s.hint}>驳回原因<textarea autoFocus value={rejectReason} onChange={(event) => setRejectReason(event.target.value)} maxLength={500} rows={3} placeholder="驳回时必填，退回草稿后可修改并重新提交" /></label>}
          {READONLY && <p className={s.hint}>当前为生产只读模式，可查看窗口，暂不能提交。</p>}
          {error && <p className={s.error} role="alert">{error}{uncertain && '。结果未确认，请先核对订单状态，暂勿重复提交。'}</p>}
        </div>
        <footer className={`${s.footer} ${tabletPopover ? s.tabletFooter : ''}`}><button type="button" onClick={() => rejectMode ? void reject() : setRejectMode(true)} disabled={READONLY || !canReject || order.status !== 'SUBMITTED' || rejectMode && !rejectReason.trim() || saving || uncertain}>{rejectMode ? '确认驳回' : '驳回'}</button><button type="button" className={s.cancel} onClick={close}>取消</button>{!rejectMode && <button type="button" className={s.primary} onClick={() => void submit()} disabled={READONLY || !canApprove || order.status !== 'SUBMITTED' || !followers.some((user) => user.id === followerId) || saving || uncertain}>{saving ? '审核中…' : ff === 'tablet' ? `通过并分配给${followers.find((user) => user.id === followerId)?.name || ''}` : '通过并分配'}</button>}{tabletPopover && <p className={s.tabletHint}>库存已齐备的会直接标记「配货齐全」。</p>}</footer>
      </>}
    </section>
  </div>;
}
